import {
  Box,
  Container,
  ListItem,
  ListItemText,
  MenuItem,
  Select,
  Tooltip,
  Typography,
} from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import DateDropZone from '../DropZones/DateDropZone';
import { resetSource } from '../../../redux/Calculations/CalculationsActions';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import { fontSize } from '../../..';
import { useSelector } from 'react-redux';
import { RootState } from '../../../redux';
import { useDispatch } from 'react-redux';
import LockOutlinedIcon from '@mui/icons-material/Lock';
import { checkIfMissingColumn } from '../../CommonFunctions/CommonFunctions';
interface IMinMaxDate {
  flowName: string;
  propKey: string;
  isFlowUsedInOtherFlowsAsSource: boolean;
}
const MinMaxDate = ({ flowName, propKey, isFlowUsedInOtherFlowsAsSource }: IMinMaxDate) => {
  const { calculations, tablesForSelectedDataSets, chartProperties } = useSelector(
    (state: RootState) => {
      return {
        calculations: state.calculations,
        tablesForSelectedDataSets: state.tabTileProps.tablesForSelectedDataSets,
        chartProperties: state.chartProperties,
      };
    },
  );
  const dispatch = useDispatch();
  const currCalculation = calculations.properties[propKey];
  const currentCalculationSession = currCalculation.currentCalculationSession;

  const activeFlow = currCalculation.currentCalculationSession?.activeFlow ?? null;
  const calculationInfo = currCalculation.currentCalculationSession?.calculationInfo ?? null;
  const source = activeFlow ? calculationInfo?.flows[activeFlow][0].source : null;
  const sourceType = activeFlow ? calculationInfo?.flows[activeFlow][0].sourceType : null;
  const getName = (): string | null => {
    if (!currentCalculationSession || !sourceType || !source || !calculationInfo) return null;
    if (sourceType[0] === 'field') return calculationInfo.fields[source[0]].displayName;
    else if (sourceType[0] === 'flow') return currentCalculationSession.flowDisplayNames[source[0]];
    return source[0];
  };

  const [isShowWarning, setIsShowWarning] = useState<boolean>(false);

  const selectedDs = chartProperties.properties[propKey].selectedDs;

  useEffect(() => {
    if (!sourceType || !source) return;
    if (sourceType[0] !== 'field') return;
    (async () => {
      try {
        const field = calculationInfo?.fields[source[0]];
        if (!field) return;
        const res = await checkIfMissingColumn(
          field,
          selectedDs,
          tablesForSelectedDataSets[selectedDs.id],
        );
        setIsShowWarning(res.isMissing);
      } catch (error) {
        console.error('Error checking missing column:', error);
        setIsShowWarning(true);
      }
    })();
  }, [source?.[0]]);

  if (
    !currentCalculationSession ||
    !calculationInfo ||
    !activeFlow ||
    !source ||
    !sourceType
  )
    return null;

  return (
    <Container
      disableGutters
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        // paddingInline: "0.5rem",
        margin: 0,
        // paddingLeft: '8px',
      }}
      maxWidth="xs"
    >
      <Box
        sx={{
          padding: '0',
          margin: '0',
          fontWeight: 'bold',
          // color: "gray",
          textAlign: 'start',
          display: 'flex',
          gap: '10px',
          justifyContent: 'space-between',
        }}
      >
        <Box
          sx={{
            fontWeight: 'bold',
            // color: "gray",
            width: 'fit-content',
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <Typography
            sx={{
              fontWeight: 'bold',
              // color: "gray",
              // fontFamily: "Axiforma Black",
              fontSize: fontSize.large,
              paddingLeft: '8px',
            }}
          >
            {flowName.replace(/\b\w/g, (char) => char.toUpperCase())}
          </Typography>
          <Typography
            sx={{
              textAlign: 'center',
              fontSize: '11px',
              color: '#999999',
              marginLeft: '0.3rem',
              fontWeight: 'bold',
            }}
          >
            ({source?.length > 0 ? 1 : 0} of 1/1)
          </Typography>
        </Box>
      </Box>
      <DateDropZone
        propKey={propKey}
        flow={calculationInfo.flows[activeFlow][0].flow }
        disableDrop={isFlowUsedInOtherFlowsAsSource}
      >
        {source.length === 0 ? (
          <p style={{ fontSize: '11px', color: '#999999', paddingLeft: '8px' }}>
            Drag the source from below column to here
          </p>
        ) : (
          <>
            <ListItem
              className="axisFilterField"
              sx={{
                border: isShowWarning ? '1px solid #850000ff !important' : '',
                borderRadius: '5px',
                marginBlock: '5px',
                marginLeft: '0.5rem',
                paddingLeft: '0.3rem',
                display: 'flex',
                justifyContent: 'flex-start',
                alignItems: 'center',
                height: '24px', // Match this height with TextField height if needed
                width: '95%',
                gap: '0.2rem',
              }}
            >
              {!isFlowUsedInOtherFlowsAsSource && (
                <button
                  type="button"
                  className="buttonCommon columnClose"
                  onClick={() => {
                    dispatch(resetSource(propKey, activeFlow, 0));
                  }}
                  title="Remove field"
                >
                  <CloseRoundedIcon style={{ fontSize: '13px' }} />
                </button>
              )}
              {isFlowUsedInOtherFlowsAsSource && (
                <Tooltip
                  title="This calculation is being used in another flow. Please remove the dependency before deleting."
                  slotProps={{
                    tooltip: {
                      sx: {
                        fontSize: '12px',
                      },
                    },
                  }}
                >
                  <LockOutlinedIcon
                    style={{
                      color: '#2BB9BB',
                    }}
                  />
                </Tooltip>
              )}
              {sourceType[0] === 'field' || sourceType[0] === 'flow' ? (
                <>
                  <ListItemText
                    primary={getName()}
                    sx={{ '& .MuiListItemText-primary': { fontSize: '11px' } }}
                  />
                  <Select
                    size="small"
                    sx={{
                      fontFamily: 'Axiforma Black',
                      fontSize: '11px',
                      backgroundColor: 'white',
                      cursor: 'auto',
                      border: 'none',
                      outline: 'none',
                      '.MuiOutlinedInput-notchedOutline': {
                        borderStyle: 'none',
                      },
                      boxShadow: 'none',
                      padding: 0,
                      '.MuiSelect-select': {
                        padding: '2px',
                      },
                      '.MuiSelect-select.MuiInputBase-input': {
                        paddingRight: '28px',
                      },
                    }}
                    defaultValue={flowName === 'minDate' ? 'min' : 'max'}
                    disabled
                  >
                    <MenuItem
                      sx={{
                        fontSize: '11px',
                      }}
                      value={flowName === 'minDate' ? 'min' : 'max'}
                      style={{
                        listStyle: 'none',
                        cursor: 'pointer',
                        color: 'black',
                        fontFamily: 'Axiforma Black',
                        fontSize: '11px',
                      }}
                      title="Window Function"
                    >
                      {flowName === 'minDate' ? 'Min' : 'Max'}
                    </MenuItem>
                  </Select>
                </>
              ) : null}
            </ListItem>
          </>
        )}
      </DateDropZone>
    </Container>
  );
};

export default MinMaxDate;
